import React from 'react';
import SidePanel from 'components/side-panel/SidePanel';
import { NavCloseIcon } from 'assets/icons';
import { logAnalyticEvent } from 'utils/analytics';
import { SettingsCleverTapEvents } from '../events';
import { VoidFn } from 'types/baseTypes';
import { CommissionBaseType, CommissionType } from '../commission-settings/constants';
import { getAmountWithCurrency } from 'utils/amountUtils';

// Constant amount values for the example (with 2 decimal points as strings)
const EXAMPLE_LINE_ITEM_UNIT_PRICE = '160.00';
const EXAMPLE_LINE_ITEM_QUANTITY = 2;
const EXAMPLE_LINE_ITEM_TOTAL = '320.00';
const EXAMPLE_DISCOUNT = '20.00';
const EXAMPLE_SUBTOTAL = '300.00';
const EXAMPLE_TAX = '50.00';
const EXAMPLE_TIP = '50.00';
const EXAMPLE_SERVICE_FEE = '100.00';
const EXAMPLE_TOTAL = '500.00';
const EXAMPLE_COST = '120.00';
const EXAMPLE_PROFIT = '200.00';
const EXAMPLE_COMMISSION_PERCENTAGE = '20';
const EXAMPLE_COMMISSION_TOTAL_VALUE = '100.00';
const EXAMPLE_SPLIT_PERCENTAGE = '100.00';

interface CommissionCalculationExampleProps {
  isOpen: boolean;
  onClose: VoidFn;
  commissionValue: string;
  commissionType: string;
  baseType: { label: string; key: string };
}

const CommissionCalculationExample: React.FC<CommissionCalculationExampleProps> = ({
  isOpen,
  onClose,
  commissionValue,
  commissionType,
  baseType
}) => {
  logAnalyticEvent(SettingsCleverTapEvents.webSettingsCommissionCalculationExampleView);

  const getCommissionOnAndValue = () => {
    const commissionOn = baseType ? baseType.label : 'total';
    if (!baseType) {
      return { commissionOn, finalCommission: EXAMPLE_COMMISSION_TOTAL_VALUE, baseAmount: parseFloat(EXAMPLE_TOTAL) };
    }
    let baseAmount = 0;
    let finalCommission = '';
    switch (baseType?.key?.toUpperCase()) {
      case CommissionBaseType.JOB_SUBTOTAL:
        baseAmount = parseFloat(EXAMPLE_SUBTOTAL);
        finalCommission = `${((parseFloat(commissionValue) / 100) * baseAmount).toFixed(2)}`;
        break;
      case CommissionBaseType.LINE_ITEM_TOTAL:
        baseAmount = parseFloat(EXAMPLE_LINE_ITEM_TOTAL);
        finalCommission = `${((parseFloat(commissionValue) / 100) * baseAmount).toFixed(2)}`;
        break;
      case CommissionBaseType.JOB_TOTAL:
        baseAmount = parseFloat(EXAMPLE_SUBTOTAL);
        finalCommission = `${((parseFloat(commissionValue) / 100) * baseAmount).toFixed(2)}`;
        break;
      case CommissionBaseType.LINE_ITEM_PROFIT:
        baseAmount = parseFloat(EXAMPLE_PROFIT);
        finalCommission = `${((parseFloat(commissionValue) / 100) * baseAmount).toFixed(2)}`;
        break;
      case CommissionBaseType.PAID_AMOUNT:
        baseAmount = parseFloat(EXAMPLE_TOTAL);
        finalCommission = `${((parseFloat(commissionValue) / 100) * baseAmount).toFixed(2)}`;
        break;
      case CommissionBaseType.NET_PAID_AMOUNT:
        baseAmount = parseFloat(EXAMPLE_TOTAL);
        finalCommission = `${((parseFloat(commissionValue) / 100) * baseAmount).toFixed(2)}`;
        break;
      case CommissionBaseType.INVOICE_TOTAL:
        baseAmount = parseFloat(EXAMPLE_TOTAL) - parseFloat(EXAMPLE_TIP);
        finalCommission = `${((parseFloat(commissionValue) / 100) * baseAmount).toFixed(2)}`;
        break;
      case CommissionBaseType.NET_MARGIN:
        baseAmount = parseFloat(EXAMPLE_TOTAL) - parseFloat(EXAMPLE_COST);
        finalCommission = `${((parseFloat(commissionValue) / 100) * baseAmount).toFixed(2)}`;
        break;
      case CommissionBaseType.GROSS_MARGIN:
        baseAmount = parseFloat(EXAMPLE_TOTAL) - parseFloat(EXAMPLE_COST);
        finalCommission = `${((parseFloat(commissionValue) / 100) * baseAmount).toFixed(2)}`;
        break;
      // case CommissionBaseType.JOB_SUBTOTAL_PROFIT:
      //   baseAmount = parseFloat(EXAMPLE_SUBTOTAL) - parseFloat(EXAMPLE_COST);
      //   finalCommission = `${((parseFloat(commissionValue) / 100) * baseAmount).toFixed(2)}`;
      //   break;
      default:
        baseAmount = parseFloat(EXAMPLE_TOTAL);
        finalCommission = EXAMPLE_COMMISSION_TOTAL_VALUE;
    }

    return { commissionOn, finalCommission, baseAmount };
  };

  const { commissionOn, finalCommission, baseAmount } = getCommissionOnAndValue();

  return (
    <SidePanel isOpen={isOpen} onClose={onClose} shouldAnimate={true}>
      <div className="flex h-full w-[375px] flex-col">
        <div className="flex items-center p-4">
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Close commission calculation example"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') onClose();
            }}
            type="button">
            <NavCloseIcon className="h-4 w-4" />
          </button>
          <h2 className="ml-2 text-[17px] font-semibold text-primaryText">Commission calculation</h2>
        </div>

        <div className="flex-1 overflow-y-auto py-6">
          <div className="rounded-lg bg-white">
            <h3 className="mb-2 text-center text-lg font-medium">Example job</h3>
            <p className="text-center text-sm text-accentText">JOB -123456</p>

            <div className="mt-6 space-y-4 text-sbase font-semibold text-primaryText">
              {/* Line Item */}
              <div className="flex justify-between px-4">
                <div>
                  <p>Sample line item</p>
                  <p className="text-[13px] text-accentText">
                    ({EXAMPLE_LINE_ITEM_QUANTITY} x ${EXAMPLE_LINE_ITEM_UNIT_PRICE})
                  </p>
                </div>
                <p>${EXAMPLE_LINE_ITEM_TOTAL}</p>
              </div>

              {/* Discount */}
              <div className="mx-4 flex justify-between border-t border-gray-200 pt-6">
                <p>Discount</p>
                <div className="flex items-center gap-4">
                  <p className="text-accentText">{getAmountWithCurrency(EXAMPLE_DISCOUNT)}</p>
                  <p className="text-primaryText">- ${EXAMPLE_DISCOUNT}</p>
                </div>
              </div>

              {/* Subtotal */}
              <div className="flex justify-between bg-highlightGray px-4 py-3">
                <p>Subtotal</p>
                <p>${EXAMPLE_SUBTOTAL}</p>
              </div>

              {/* Additional Fees */}
              <div className="space-y-4 px-4">
                <div className="flex justify-between">
                  <p>Tax</p>
                  <p>${EXAMPLE_TAX}</p>
                </div>
                <div className="flex justify-between">
                  <p>Tip</p>
                  <p>${EXAMPLE_TIP}</p>
                </div>
                <div className="flex justify-between">
                  <p>Service fee</p>
                  <p>${EXAMPLE_SERVICE_FEE}</p>
                </div>
              </div>

              {/* Total */}
              <div className="flex justify-between bg-highlightGray px-4 py-3">
                <p>Total</p>
                <p>${EXAMPLE_TOTAL}</p>
              </div>

              {/* Cost & Profit */}
              <div className="space-y-4 px-4">
                <div className="flex justify-between">
                  <p>Cost</p>
                  <p>${EXAMPLE_COST}</p>
                </div>
                <div className="flex justify-between">
                  <p>Profit</p>
                  <p>${EXAMPLE_PROFIT}</p>
                </div>
              </div>

              {/* Commission Details */}
              <div className="mx-4 space-y-2 border-t border-gray-200 pt-4">
                <div className="flex justify-between">
                  <p>Rate</p>
                  <div className="text-right">
                    {commissionType === CommissionType.PERCENTAGE || !commissionValue ? (
                      <p>{commissionValue || EXAMPLE_COMMISSION_PERCENTAGE}%</p>
                    ) : (
                      <p>-</p>
                    )}
                    {(commissionType === CommissionType.PERCENTAGE || !commissionValue) && (
                      <p className="text-[13px] text-accentText">({EXAMPLE_SPLIT_PERCENTAGE}% split)</p>
                    )}
                  </div>
                </div>
                <div className="flex justify-between">
                  <p>Commission on</p>
                  {commissionType === CommissionType.PERCENTAGE || !commissionValue ? (
                    <div className="text-right">
                      <p>${baseAmount}</p>
                      <p className="text-[13px] text-accentText">({commissionOn})</p>
                    </div>
                  ) : (
                    <p>-</p>
                  )}
                </div>
              </div>

              {/* Final Commission */}
              <div className="mx-4 flex justify-between border-t border-gray-200 pt-4 text-[17px]">
                <p className="font-bold">Commission</p>
                <div className="text-right">
                  <p className="font-bold">
                    {commissionType === CommissionType.PERCENTAGE || !commissionValue
                      ? `$${finalCommission}`
                      : getAmountWithCurrency(commissionValue)}
                  </p>
                  {(commissionType === CommissionType.PERCENTAGE || !commissionValue) && (
                    <p className="text-[13px] text-accentText">
                      ({commissionValue || EXAMPLE_COMMISSION_PERCENTAGE}% of ${baseAmount || EXAMPLE_TOTAL})
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SidePanel>
  );
};

export default CommissionCalculationExample;
